<template>
    <div
        class="py-6 px-10"
        style="align-self: start; flex-grow: 1; width: 100%"
    >
        <v-overlay v-model="showProgressLibrary" opacity="0.8">
            <FullscreenLoader />
        </v-overlay>

        <span v-if="!showProgressLibrary">
            <div class="mb-10">
                <v-row class="mt-6 mb-0 ml-6">
                    <v-col cols="10" class="pa-0 mx-0">
                        <span
                            class="
                                text-display-large text-left
                                font-weight-regular
                                text-typography1
                            "
                        >
                            Browse our extensive calculation libraries...
                        </span>
                    </v-col>
                </v-row>
                <v-row class="ml-6 mb-6 mt-2">
                    <v-col cols="10" class="pa-0 mx-0 mt-0 mb-6">
                        <span
                            class="
                                text-display-small text-left text-typography2
                            "
                        >
                            Use the search bar above or browse our extensive
                            selection of calculation libraries below
                        </span>
                    </v-col>
                </v-row>
            </div>

            <v-row
                class="ml-6 mb-10 mr-6"
                align="center"
                justify="space-between"
            >
                <div>
                    <span class="mr-2"> Filter libraries by type: </span>

                    <span
                        v-for="(category, i) in libCategoriesToDisplay"
                        :key="i"
                    >
                        <v-tooltip
                            location="top"
                            max-width="300px"
                            color="secondary"
                        >
                            <template v-slot:activator="{ props }">
                                <v-btn
                                    rounded
                                    size="small"
                                    class="
                                        text-capitalize
                                        button-group
                                        text-label-medium
                                        bg-grey-lighten-4
                                    "
                                    :style="[
                                        index === i && {
                                            filter: 'brightness(0.8)',
                                        },
                                    ]"
                                    @click="categoryClicked(parseInt(i))"
                                    v-bind="props"
                                    variant="elevated"
                                >
                                    {{ category }}
                                </v-btn>
                            </template>
                            <span> {{ categoryDescriptions[i] }} </span>
                        </v-tooltip>
                    </span>
                </div>

                <div>
                    <NewDialog :libraries="libraries" />
                </div>
            </v-row>

            <div class="cards-grid">
                <Card
                    v-for="library in librariesToDisplay"
                    :key="library.libraryId"
                    :library="library"
                    :libraries="libraries"
                />
            </div>
        </span>
    </div>
</template>

<script setup>
import Card from './Card.vue'
import NewDialog from './NewDialog.vue'
import FullscreenLoader from '../../shared/FullscreenLoader.vue'
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'

const store = useStore()
const index = ref(1)
const libraries = ref([])
const loading = ref(true)

const showProgressLibrary = computed(() => store.state.showProgressLibrary)
const libCategories = computed(() => store.state.libCategories)
const user = computed(() => store.state.user)
const admins = computed(() => store.state.admins)
const isExternalFacing = computed(() => store.state.isExternalFacing)

const categoryDescriptions = {
    1: 'Libraries ready for use by all of Arup',
    2: 'These libraries are the engine behind other apps and services - as such they are not optimised for use via the web, but feel free to take a look around and see what people are using ArupCompute for!',
    3: "Trials, experiments, the new and unrefined. Don't expect things in here to work, and do not rely on them for production. Perhaps you will stumble across an interesting idea or colleague by browsing this collection.",
    4: 'These libraries are superseded, but are kept running to power existing scripts and services.',
}

const librariesToDisplay = computed(() => {
    if (loading.value) return []
    const libraryWithVersions = libraries.value.filter(
        (lib) => lib.libraryVersions
    )
    if (index.value === 0) return libraryWithVersions
    let filter = libraryWithVersions.filter(
        (lib) => lib.libraryVersions[0].libraryVersionStatusId === index.value
    )
    if (index.value === 3) {
        for (const i in libraries.value) {
            const lib = libraries.value[i]
            if (
                lib.libraryVersions &&
                !lib.libraryVersions[0].libraryVersionStatusId
            ) {
                filter.push(lib)
            }
        }
    }
    return filter
})

const libCategoriesToDisplay = computed(() => {
    if (isExternalFacing.value) {
        if (admins.value.includes(user.value?.mail.toLowerCase())) {
            return Object.fromEntries(
                Object.entries(libCategories.value).filter(
                    ([key]) => key === '1' || key === '3'
                )
            )
        } else {
            return Object.fromEntries(
                Object.entries(libCategories.value).filter(
                    ([key]) => key === '1'
                )
            )
        }
    }
    return libCategories.value
})

function categoryClicked(i) {
    index.value = i
}

onMounted(async () => {
    libraries.value = await store.dispatch('getLibrariesCached', false)
    loading.value = false
})
</script>

<style scoped>
.button-group {
    margin: 10px;
}

.cards-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
    gap: 24px;
}
</style>
